/**
 * reorder-drag.ts — Logic thuần của kéo-thả sắp xếp danh sách dọc bằng ngón tay (hộp "Sửa phân đoạn" Hộp thư di động).
 * Giao diện (pointer events + tự cuộn) ở pages/conversations/mobile-segments-sheet.tsx.
 *
 * Toạ độ: mọi số là toạ độ DỌC trong cùng một hệ (vd. tính từ mép trên danh sách), nên cuộn nội dung không làm sai phép tính.
 */

/**
 * Vị trí thả: chỉ số mà hàng đang kéo sẽ có trong danh sách SAU KHI bỏ nó ra rồi chèn lại.
 * `mids` = tâm dọc của MỌI hàng (kể cả hàng đang kéo) lúc bắt đầu kéo; `from` = chỉ số hàng đang kéo; `center` = tâm hiện tại của hàng đang kéo.
 * Hàng đang kéo vượt qua tâm hàng nào thì đứng sau hàng đó.
 */
export function dropIndex(mids: number[], from: number, center: number): number {
  let to = 0
  for (let i = 0; i < mids.length; i++) {
    if (i === from) continue
    if (mids[i] < center) to++
  }
  return to
}

/** Đưa phần tử `from` tới chỉ số `to` (theo nghĩa của `dropIndex`). Ngoài phạm vi / không đổi → trả lại chính mảng. */
export function moveTo<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list
  const next = list.slice()
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

export interface RowBox { top: number; height: number }

/**
 * Vị trí dọc của vạch chỉ chỗ thả: mép trên của hàng sẽ đứng sau hàng đang kéo (hoặc mép dưới hàng cuối nếu thả cuối).
 * `rows` = hộp của mọi hàng lúc bắt đầu kéo (kể cả hàng đang kéo). Danh sách rỗng → 0.
 */
export function dropLineY(rows: RowBox[], from: number, to: number): number {
  const others = rows.filter((_, i) => i !== from)
  if (others.length === 0) return 0
  if (to >= others.length) {
    const last = others[others.length - 1]
    return last.top + last.height
  }
  return others[Math.max(0, to)].top
}

/**
 * Kẹp độ dời dọc của hàng đang kéo để nó không ra khỏi khối các hàng kéo được: [mép trên khối, mép dưới khối].
 * Quan trọng với danh sách cuộn: hàng bị `translateY` ra ngoài làm vùng cuộn phình ra → tự cuộn kéo theo → chạy mãi không dừng.
 */
export function clampDragDy(box: RowBox, blockTop: number, blockBottom: number, dy: number): number {
  const min = blockTop - box.top
  const max = blockBottom - (box.top + box.height)
  return Math.min(max, Math.max(min, dy))
}

/**
 * Tốc độ tự cuộn (px / khung hình) khi ngón tay kéo sát mép khung cuộn: âm = cuộn lên, dương = cuộn xuống, 0 = ở giữa.
 * Càng sát mép (hoặc vượt ra ngoài) càng nhanh, tối đa `maxSpeed`.
 */
export function autoScrollDelta(pointerY: number, viewTop: number, viewBottom: number, edge = 56, maxSpeed = 14): number {
  if (viewBottom - viewTop < edge * 2) return 0 // khung quá thấp: không tự cuộn (tránh giật)
  if (pointerY < viewTop + edge) return -Math.round(maxSpeed * Math.min(1, (viewTop + edge - pointerY) / edge))
  if (pointerY > viewBottom - edge) return Math.round(maxSpeed * Math.min(1, (pointerY - (viewBottom - edge)) / edge))
  return 0
}
